/**
 * Documents Service
 *
 * Service for downloading PDF documents (receipts, tax documents, etc.)
 */

import { getApiClient } from './client';

/**
 * Supported document types
 */
export type DocumentType = 'transaction-receipt' | 'tax-receipt' | 'donation-summary';

/**
 * Download PDF document
 *
 * @param documentType - Type of document to download
 * @param transactionId - Transaction or document ID
 * @param companyId - Company ID
 * @returns Blob containing PDF data
 */
export async function downloadPdfDocument(
  documentType: DocumentType,
  transactionId: number,
  companyId: number
): Promise<Blob> {
  const client = getApiClient();

  const response = await client.get<Blob>(
    `/documents/pdf/${companyId}/${documentType}/${transactionId}`,
    {
      responseType: 'blob',
    }
  );

  // Ensure we're actually getting a Blob
  if (!(response.data instanceof Blob)) {
    throw new Error('Response is not a valid PDF blob');
  }

  return response.data;
}

/**
 * Download and trigger browser download for PDF document
 *
 * @param documentType - Type of document to download
 * @param transactionId - Transaction or document ID
 * @param companyId - Company ID
 * @param filename - Optional custom filename (default: auto-generated)
 */
export async function downloadAndSavePdf(
  documentType: DocumentType,
  transactionId: number,
  companyId: number,
  filename?: string
): Promise<void> {
  try {
    const blob = await downloadPdfDocument(documentType, transactionId, companyId);

    console.log('[downloadAndSavePdf] Blob received:', {
      size: blob.size,
      type: blob.type,
      isBlob: blob instanceof Blob,
    });

    // Validate blob
    if (!blob || blob.size === 0) {
      throw new Error('Received empty or invalid PDF data');
    }

    // Generate default filename if not provided
    const defaultFilename = getDefaultFilename(documentType, transactionId);
    const finalFilename = filename || defaultFilename;

    // Create blob URL and trigger download
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = finalFilename;
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    throw error;
  }
}

/**
 * Open PDF in new browser tab
 *
 * @param documentType - Type of document to download
 * @param transactionId - Transaction or document ID
 * @param companyId - Company ID
 */
export async function openPdfInNewTab(
  documentType: DocumentType,
  transactionId: number,
  companyId: number
): Promise<void> {
  try {
    const blob = await downloadPdfDocument(documentType, transactionId, companyId);

    console.log('[openPdfInNewTab] Blob received:', {
      size: blob.size,
      type: blob.type,
      isBlob: blob instanceof Blob,
    });

    // Validate blob
    if (!blob || blob.size === 0) {
      throw new Error('Received empty or invalid PDF data');
    }

    // Create blob URL and open in new tab
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');

    // Cleanup after a delay to allow the new tab to load
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 1000);
  } catch (error) {
    console.error('Error opening PDF:', error);
    throw error;
  }
}

/**
 * Get default filename based on document type
 */
function getDefaultFilename(documentType: DocumentType, transactionId: number): string {
  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  switch (documentType) {
    case 'transaction-receipt':
      return `receipt_${transactionId}_${timestamp}.pdf`;
    case 'tax-receipt':
      return `tax_receipt_${transactionId}_${timestamp}.pdf`;
    case 'donation-summary':
      return `donation_summary_${transactionId}_${timestamp}.pdf`;
    default:
      return `document_${transactionId}_${timestamp}.pdf`;
  }
}
